import React from "react";
import cn from "clsx";
import { isArray } from "../../utils/helpers";
import "./InputToggleable.css";

export type InputToggleableValue = boolean | string | null;

export interface InputToggleableObject {
  /**
   * The ID of the input element.
   */
  id: string;
  /**
   * The name of the input element.
   */
  name: string;
  /**
   * The value of the input element.
   *
   * Sadly, using react-final-form means it could be an array...
   */
  value: InputToggleableValue | InputToggleableValue[];
  /**
   * A label to display next to the input element.
   */
  label?: React.ReactNode;
  /**
   * Control if the input element is disabled or not.
   */
  disabled?: boolean;
  /**
   * Adds an extra class to turn into a switch.
   */
  switchMode?: boolean;
  /**
   * Extra class names to apply to the toggleable.
   */
  className?: string;
  /**
   * Event handler that fires when the value changes.
   */
  onChange: React.ChangeEventHandler<HTMLInputElement>;
}

export interface InputToggleableProps extends InputToggleableObject {
  /**
   * The type of toggleable input.
   *
   * @default checkbox
   */
  type: "checkbox" | "radio";
}

const InputToggleable = ({
  type = "checkbox",
  id,
  name,
  value,
  label,
  disabled = false,
  switchMode = false,
  className,
  onChange
}: InputToggleableProps) => {
  // Final Form likes to save checkbox values as arrays,
  // so we need to unpack them...
  const _value = isArray(value) ? (value as InputToggleableValue[])[0] : value;

  return (
    <label
      className={cn(
        "input-toggleable",
        `input-type-${type}`,
        !!switchMode && "is-switch",
        !!label && "has-label",
        !!_value && "is-checked",
        disabled && "is-disabled",
        className
      )}
    >
      <span className="input-toggleable-control">
        <input
          type={type}
          id={id}
          name={name}
          checked={Boolean(_value)}
          disabled={disabled}
          onChange={onChange}
        />
      </span>
      {!!label && <span className="input-toggleable-label">{label}</span>}
    </label>
  );
};

export default InputToggleable;
